import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { StylesProvider } from "@mui/styles";
import ThemeLoader from "../../theme/themeControls/ThemeLoader";
import Routes from "../../routes/Routes";
import {
  SET_SERVER_ERROR,
  SHOW_SUCCESS_NOTIFICATION,
  SHOW_DELETE_NOTIFICATION,
  SHOW_SSO_MODAL,
  SHOW_SECURITY_QUESTION_MODAL,
  UPDATE_WINDOW_SIZE,
  UPDATE_WINDOW_POSITION,
  UPDATE_HIGHLIGHT_COLOR,
  LIGHT_THEME,
  DARK_THEME,
  UPDATE_DISPLAY_MODE,
} from "../../redux/constants";
import axios from "axios";
import GenericErrorModal from "../Modals/GenericErrorModal/GenericErrorModal";
import SecurityQuestionModal from "../Modals/SecurityQuestionModal/SecurityQuestionModal";
import cookie from "react-cookies";
import { user_current } from "../../api/shared/signin";
import { getAdvisorSecurityQuestion } from "../../api/shared/userProfile";
import { getAdvisorAppearance } from "../../api/shared/signin";
import "../../../node_modules/@instream-solutions/instream-design-system/src/UX/index.scss";
import "./App.scss";
import "../../vendor/i18n";

const HEARTBEAT_TIMEOUT = 60 * 1000;

const NOTIFICATION_BLACK_LIST = [
  "j_spring_security_check",
  "j_spring_security_logout",
  "user/current",
  "info/getImage",
  "property/calculate",
  "calculate",
  "signup/calculate-billing",
  "signup/validate-signup-email",
  "signup/firm",
  "forgotpwdvalidatemail",
  "forgotpasswordreset",
  "securityquestion",
  "subscription-details",
  "delete-default-zero-income-expense",
];

const isUrlInNotificationBlackList = (url) => {
  return NOTIFICATION_BLACK_LIST.find((item) => {
    return url.includes(item);
  });
};

function App({
  theme,
  uxState,
  currentUser,
  setServerError,
  showSuccessNotification,
  showDeleteNotification,
  showSSOModal,
  showSecurityQuestionModal,
  updateWindowPosition,
  updateWindowSize,
  updateHighlightColor,
  updateDisplayMode,
}) {
  const [state, setState] = useState({
    intervalTimer: null,
    axiosInterceptor: null,
  });

  useEffect(() => {
    axios.interceptors.request.use(
      (config) => {
        config.withCredentials = true;

        if (isUrlInNotificationBlackList(config.url)) return config;

        if (!cookie.load("CURRENT_USER_COOKIE")) {
          showSSOModal();
          return new axios.Cancel("user is no longer logged in.");
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      (response) => {
        if (isUrlInNotificationBlackList(response.config.url)) return response;

        if (
          response.config.method.toUpperCase() === "POST" ||
          response.config.method.toUpperCase() === "PUT"
        ) {
          showSuccessNotification();
        } else if (response.config.method.toUpperCase() === "DELETE") {
          showDeleteNotification();
        }

        return response;
      },
      (error) => {
        setServerError();
        return Promise.reject(error);
      }
    );
  }, []);

  useEffect(() => {
    const loggedInUser = currentUser.toJS();

    if (loggedInUser.id === null) {
      if (state.intervalTimer !== null) {
        clearInterval(state.intervalTimer);

        setState({
          ...state,
          intervalTimer: null,
        });
      }
      return;
    }

    if (state.intervalTimer !== null) return;

    const sessionCheckInterval = setInterval(() => {
      if (!cookie.load("CURRENT_USER_COOKIE")) {
        clearInterval(state.intervalTimer);
        showSSOModal();
        return;
      }

      user_current().then(
        (response) => {
          if (response.data.payload.userId) return;
          clearInterval(state.intervalTimer);
          showSSOModal();
        },
        () => {
          clearInterval(state.intervalTimer);
          showSSOModal();
        }
      );
    }, HEARTBEAT_TIMEOUT);

    setState({
      ...state,
      intervalTimer: sessionCheckInterval,
    });
  }, [currentUser]);

  useEffect(() => {
    const loggedInUser = currentUser.toJS();

    if (loggedInUser.id === null) {
      return;
    }

    const advisorId = loggedInUser.advisor.advisorId;

    getAdvisorSecurityQuestion(advisorId).then((response) => {
      const data = response.data.payload;
      if (data.questionNum === null) {
        showSecurityQuestionModal();
      }
    });
  }, [currentUser]);

  useEffect(() => {
    const loggedInUser = currentUser.toJS();

    if (loggedInUser.id === null) {
      return;
    }

    const window = cookie.load("instream_window");

    if (window) {
      updateWindowPosition(window);
      updateWindowSize(window);
    }
  }, [currentUser]);

  useEffect(() => {
    const loggedInUser = currentUser.toJS();

    if (loggedInUser.id === null) {
      return;
    }

    const advisorId = loggedInUser.advisor.advisorId;

    getAdvisorAppearance(advisorId).then((response) => {
      updateDisplayMode(response.mode ? LIGHT_THEME : DARK_THEME);
      updateHighlightColor(response.colorTheme);
    });
  }, [currentUser]);

  return (
    <StylesProvider injectFirst>
      <ThemeLoader>
        <Routes />
        <GenericErrorModal />
        {uxState.getIn(["showSecurityQuestionModal"]) && (
          <SecurityQuestionModal />
        )}
      </ThemeLoader>
    </StylesProvider>
  );
}

export default connect(
  (store) => ({
    theme: store.themeReducer,
    uxState: store.uxStateReducer,
    currentUser: store.currentUserReducer,
  }),
  (dispatch) => {
    return {
      setServerError: () =>
        dispatch({
          type: SET_SERVER_ERROR,
        }),
      showSuccessNotification: () =>
        dispatch({
          type: SHOW_SUCCESS_NOTIFICATION,
        }),
      showDeleteNotification: () =>
        dispatch({
          type: SHOW_DELETE_NOTIFICATION,
        }),
      showSecurityQuestionModal: () =>
        dispatch({
          type: SHOW_SECURITY_QUESTION_MODAL,
        }),
      showSSOModal: () =>
        dispatch({
          type: SHOW_SSO_MODAL,
        }),
      updateWindowSize: (e) =>
        dispatch({
          type: UPDATE_WINDOW_SIZE,
          data: e,
        }),
      updateWindowPosition: (e) =>
        dispatch({
          type: UPDATE_WINDOW_POSITION,
          data: e,
        }),
      updateDisplayMode: (value) =>
        dispatch({
          type: UPDATE_DISPLAY_MODE,
          value: value,
        }),
      updateHighlightColor: (value) =>
        dispatch({
          type: UPDATE_HIGHLIGHT_COLOR,
          value: value,
        }),
    };
  }
)(App);
